import { render } from 'preact';
import { App } from './ui/App.js';
import { registerServiceWorker } from './sw/register.js';
import { bootPublisherMode } from './publisher/mode.js';
import { applyPublisherBrand } from './publisher/brand.js';
import { installBuilderAnalyticsBeacon } from './telemetry/builder-beacon.js';
import { fetchPlaylistDirectory } from './video/playlist-store.js';
import { currentBible } from './state/bible-store.js';
import { route } from './state/store.js';
import { track } from './state/telemetry.js';
import { initVault } from './state/security/vault.js';
import { initPaa } from './state/paa.js';
import './styles.css';

const root = document.getElementById('app');
if (!root) throw new Error('Missing #app root');

void initVault();
// PAA (Persistent App Availability): if the published shell injected
// window.__PAA_URL__, fetch + verify the signed healthy-proxy list and point
// API/media traffic at a healthy neutral domain. Inert otherwise.
void initPaa();

// Wire telemetry → Builder's /api/analytics/event so every Reader event
// (app_open, chapter view, etc.) flows into the same dashboard as the
// Builder itself. Cross-origin POST via sendBeacon. No-op if the Builder
// is unreachable; never blocks the boot.
installBuilderAnalyticsBeacon();
void fetchPlaylistDirectory();

/**
 * Normalise whatever appName comes out of publisher.json into the canonical
 * "{Token} Audio Bible" format. Handles both the current format and the
 * legacy "Language [Version] Bible, FCBH" format so existing partner deploys
 * don't need to be regenerated.
 *
 *   "Hausa Audio Bible"        → unchanged
 *   "ESV Audio Bible"          → unchanged
 *   "Hausa Bible, FCBH"        → "Hausa Audio Bible"
 *   "Hausa ESV Bible, FCBH"    → "ESV Audio Bible"   (version present → lead with it)
 */
function normaliseAppName(raw: string): string {
  // Already in correct format — nothing to do.
  if (raw.includes('Audio Bible')) return raw;

  // Strip the legacy ", FCBH" suffix.
  const stripped = raw.replace(/,\s*FCBH\s*$/i, '').trim();

  // "Language Version Bible" → version is the differentiator, lead with it.
  const versioned = stripped.match(/^(\S+)\s+(\S+)\s+Bible$/i);
  if (versioned) return `${versioned[2]} Audio Bible`;

  // "Language Bible" → language-only form.
  const simple = stripped.match(/^(.+)\s+Bible$/i);
  if (simple) return `${simple[1]!.trim()} Audio Bible`;

  // Unrecognised — append suffix so something reasonable appears.
  return `${stripped} Audio Bible`;
}

/**
 * Patch the <link rel="manifest"> so the OS uses the correct name when the
 * user taps "Add to Home Screen". Runs before render so the browser sees the
 * name before any install prompt fires.
 *
 * Two call-sites:
 *  • Publisher mode  — uses manifest.appName from publisher.json
 *  • Base reader     — derives a name from the currently loaded Bible
 */
function patchPwaName(appName: string): void {
  const name = normaliseAppName(appName);
  document.title = name;

  const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) return;

  // Word-boundary short_name: take whole words up to 12 chars.
  const words = name.split(' ');
  let short = '';
  for (const w of words) {
    const candidate = short ? `${short} ${w}` : w;
    if (candidate.length > 12) break;
    short = candidate;
  }
  const shortName = short || name.slice(0, 12);

  fetch(link.href)
    .then((r) => r.json())
    .then((base: Record<string, unknown>) => {
      const patched = { ...base, name, short_name: shortName };
      const blob = new Blob([JSON.stringify(patched)], {
        type: 'application/manifest+json',
      });
      link.href = URL.createObjectURL(blob);
    })
    .catch(() => {
      // Non-fatal — static manifest stays as fallback.
    });
}

/**
 * Derive a home-screen name from a raw Bible Brain displayName.
 * "English Standard Version" → "English Standard Version Audio Bible"
 * Used for the base reader when no publisher.json is present.
 */
function appNameFromDisplayName(displayName: string): string {
  return `${displayName} Audio Bible`;
}

// Publisher-mode boot — fires once, fires fast. If /data/publisher.json
// exists, swap in the partner's bibleId + initial chapter BEFORE the App
// mounts so the first paint uses the right content. If not, the Reader
// continues with its current ESV/BB defaults.
void bootPublisherMode().then((manifest) => {
  if (manifest) {
    currentBible.value = {
      bibleId: manifest.bibleId,
      languageIso: manifest.languageIso,
      displayName: manifest.displayName,
    };
    route.value = {
      book: manifest.initialChapter.bookId,
      chapter: manifest.initialChapter.chapter,
    };
    // Partner brand override — accent + manifest theme color. Runs BEFORE
    // render so the first paint already wears the partner palette.
    applyPublisherBrand(manifest);
    // Patch PWA name from publisher manifest. normaliseAppName migrates any
    // legacy "Language Bible, FCBH" format from older builder deploys.
    patchPwaName(manifest.appName);
  } else {
    // Base reader (no publisher.json) — derive name from the loaded Bible so
    // the home-screen label is "English Standard Version Audio Bible" etc.
    // rather than the static fallback "Audio Bible".
    patchPwaName(appNameFromDisplayName(currentBible.value.displayName));
  }
  render(<App />, root);
  registerServiceWorker();
  // One app_open per mount — the only Reader event the Builder route
  // accepts today. Carries 'publisher' vs 'esv' mode as a side-prop;
  // the route strips unrecognized keys before dispatch.
  track('app_open', { mode: manifest ? 'publisher' : 'esv' });
});
